import React, { useEffect, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  formatLocalDate,
  isWorkingDay,
  parseLocalDate,
} from "../../lib/workingHours";

interface WorkHoursDatePopoverProps {
  value: string | null;
  onChange: (date: string | null) => void;
  ariaLabel?: string;
  placeholder?: string;
  // 0=Sun..6=Sat — matches the convention used by the rest of the app
  // (FullCalendar's `firstDay`, useCalendarEvents). Defaults to Sunday.
  weekStartDay?: number | null;
}

function formatMonthYear(d: Date): string {
  return d.toLocaleString("default", { month: "long", year: "numeric" });
}

function formatDisplayDate(s: string): string {
  return parseLocalDate(s).toLocaleDateString("default", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Indexed Sun..Sat — rotated per `weekStartDay` when rendering.
const DAY_LABELS_SUN_FIRST = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Inline date popover for the "Working hours only" path. Renders a month
 * grid with Sat/Sun cells non-clickable, so the user literally can't pick
 * a weekend — matching the "make invalid choices unreachable" policy.
 *
 * Used in place of the native <input type="date"> for constrained tasks
 * because HTML date inputs can't disable individual weekdays.
 */
const WorkHoursDatePopover: React.FC<WorkHoursDatePopoverProps> = ({
  value,
  onChange,
  ariaLabel = "Scheduled date",
  placeholder = "Set date",
  weekStartDay,
}) => {
  const weekStart = ((weekStartDay ?? 0) % 7 + 7) % 7;
  const dayLabels = [
    ...DAY_LABELS_SUN_FIRST.slice(weekStart),
    ...DAY_LABELS_SUN_FIRST.slice(0, weekStart),
  ];
  const [open, setOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(() => {
    if (value) {
      const d = parseLocalDate(value);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const d = parseLocalDate(value);
      setDisplayMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const year = displayMonth.getFullYear();
  const month = displayMonth.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  // Shift the leading-blanks count so column 0 lines up with the user's
  // week-start day. (firstOfMonth.getDay() - weekStart + 7) % 7.
  const leadingBlanks = (firstOfMonth.getDay() - weekStart + 7) % 7;
  const daysInMonth = lastOfMonth.getDate();
  const today = formatLocalDate(new Date());

  type Cell = { key: string; dateStr: string | null; day: number | null };
  const cells: Cell[] = [];
  for (let i = 0; i < leadingBlanks; i++) {
    cells.push({ key: `blank-${i}`, dateStr: null, day: null });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = formatLocalDate(new Date(year, month, d));
    cells.push({ key: dateStr, dateStr, day: d });
  }

  const goPrev = () => setDisplayMonth(new Date(year, month - 1, 1));
  const goNext = () => setDisplayMonth(new Date(year, month + 1, 1));

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center bg-warm-bg-elevated border border-warm-border rounded-soft pl-10 pr-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-warm-blue transition-colors"
      >
        <Calendar
          className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-text-tertiary"
          size={16}
        />
        <span
          className={
            value ? "text-warm-text-primary" : "text-warm-text-tertiary"
          }
        >
          {value ? formatDisplayDate(value) : placeholder}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onChange(null);
                setOpen(false);
              }
            }}
            className="ml-auto p-0.5 rounded-full hover:bg-warm-bg-base text-warm-text-tertiary hover:text-warm-text-secondary transition-colors"
          >
            <X size={14} />
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Pick a weekday"
          className="absolute top-full left-0 mt-1.5 z-50 bg-warm-bg-card border border-warm-border rounded-soft shadow-soft-lg p-3 w-72 animate-popover-in origin-top"
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              aria-label="Previous month"
              onClick={goPrev}
              className="p-1 rounded-full text-warm-text-secondary hover:bg-warm-bg-elevated transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-sm font-medium text-warm-text-primary">
              {formatMonthYear(displayMonth)}
            </div>
            <button
              type="button"
              aria-label="Next month"
              onClick={goNext}
              className="p-1 rounded-full text-warm-text-secondary hover:bg-warm-bg-elevated transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {dayLabels.map((lbl, i) => {
              // Visually dim the weekend headers regardless of where they
              // land in the row (Sun-first: cols 0/6; Mon-first: cols 5/6).
              const sundayCol = (7 - weekStart) % 7;
              const saturdayCol = (6 - weekStart + 7) % 7;
              const isWeekendHeader = i === sundayCol || i === saturdayCol;
              return (
                <div
                  key={i}
                  className={`text-center text-[10px] font-medium uppercase tracking-wider ${
                    isWeekendHeader
                      ? "text-warm-text-tertiary/60"
                      : "text-warm-text-tertiary"
                  }`}
                >
                  {lbl}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell) => {
              if (cell.dateStr === null) {
                return <div key={cell.key} className="aspect-square" />;
              }
              const isValid = isWorkingDay(cell.dateStr);
              const isSelected = cell.dateStr === value;
              const isToday = cell.dateStr === today;
              return (
                <button
                  key={cell.key}
                  type="button"
                  disabled={!isValid}
                  aria-label={cell.dateStr}
                  onClick={() => {
                    if (!isValid || cell.dateStr === null) return;
                    onChange(cell.dateStr);
                    setOpen(false);
                  }}
                  className={`aspect-square text-xs rounded-soft transition-colors ${
                    !isValid
                      ? "text-warm-text-tertiary/40 cursor-not-allowed"
                      : isSelected
                        ? "bg-warm-blue text-white font-medium"
                        : isToday
                          ? "text-warm-blue font-medium ring-1 ring-warm-blue/40 hover:bg-warm-bg-elevated"
                          : "text-warm-text-primary hover:bg-warm-bg-elevated"
                  }`}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkHoursDatePopover;
